import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { 
  AreaChart,
  Area,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer
} from 'recharts';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  TimeScale,
  Filler
} from 'chart.js';
import { Line as ChartJSLine } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { backtestingApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { formatCurrency, formatCryptoPrice } from '@/utils/formatters';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  ChartLegend,
  TimeScale,
  Filler
);

interface Trade {
  date: string;
  action: 'BUY' | 'SELL';
  price: number;
  amount: number;
  value: number;
  fees: number;
  reason: string;
}

interface EquityPoint {
  date: string;
  value: number;
  balance: number;
  position: number;
}

interface HistoricalPrice {
  timestamp: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface BacktestDetails {
  id: string;
  symbol: string;
  strategyName: string;
  strategyType: string;
  startDate: string;
  endDate: string;
  initialBalance: number;
  finalBalance: number;
  totalReturn: number;
  sharpeRatio: number;
  maxDrawdown: number;
  winRate: number;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  results: {
    tradeDetails: Trade[];
    equityCurve: EquityPoint[];
    avgTrade: number;
    largestWin: number;
    largestLoss: number;
    profitFactor: number;
    volatility: number;
  };
  historicalPrices: HistoricalPrice[];
}

interface BacktestDetailsModalProps {
  backtestId: string;
  onClose: () => void;
}

const BacktestDetailsModal: React.FC<BacktestDetailsModalProps> = ({ backtestId, onClose }) => {
  const [backtest, setBacktest] = useState<BacktestDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'trades' | 'equity'>('overview');

  useEffect(() => {
    loadBacktestDetails();
  }, [backtestId]);

  const loadBacktestDetails = async () => {
    try {
      setIsLoading(true);
      const response = await backtestingApi.getBacktestById(backtestId);
      const backtestData = (response as any).data?.backtest;
      setBacktest(backtestData);
    } catch (error: any) {
      console.error('Failed to load backtest details:', error);
      toast.error('Failed to load backtest details');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-center text-gray-900 dark:text-white">Loading backtest details...</p>
        </div>
      </div>
    );
  }

  if (!backtest) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8 max-w-md w-full mx-4">
          <p className="text-center text-gray-900 dark:text-white">Backtest not found</p>
          <button
            onClick={onClose}
            className="mt-4 w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-y-auto my-4">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {backtest.strategyName} - {backtest.symbol}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {new Date(backtest.startDate).toLocaleDateString()} to {new Date(backtest.endDate).toLocaleDateString()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700 px-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Overview & Chart
            </button>
            <button
              onClick={() => setActiveTab('trades')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'trades'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Trade History
            </button>
            <button
              onClick={() => setActiveTab('equity')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'equity'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300'
              }`}
            >
              Equity Curve & Metrics
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Performance Summary Cards - Always Visible */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Total Return</div>
              <div className={`mt-1 text-2xl font-bold ${backtest.totalReturn > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {backtest.totalReturn > 0 ? '+' : ''}{backtest.totalReturn.toFixed(2)}%
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Win Rate</div>
              <div className="mt-1 text-2xl font-bold text-green-600">
                {isNaN(backtest.winRate) || backtest.winRate === null ? 'N/A' : `${backtest.winRate.toFixed(1)}%`}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Sharpe Ratio</div>
              <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                {isNaN(backtest.sharpeRatio) || backtest.sharpeRatio === null ? 'N/A' : backtest.sharpeRatio.toFixed(2)}
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Max Drawdown</div>
              <div className="mt-1 text-2xl font-bold text-red-600">
                -{Math.abs(backtest.maxDrawdown).toFixed(2)}%
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Price Chart with Trade Signals - Chart.JS Version */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Price Chart with Trade Signals
                </h3>
                {(backtest.historicalPrices || []).length > 0 ? (
                  <div style={{ position: 'relative', height: '400px', width: '100%' }}>
                    <ChartJSLine
                      data={{
                        datasets: [
                          {
                            label: `${backtest.symbol} Close Price`,
                            data: (backtest.historicalPrices || []).map(price => ({
                              x: new Date(price.timestamp),
                              y: price.close
                            })),
                            borderColor: '#2563eb',
                            backgroundColor: 'rgba(37, 99, 235, 0.1)',
                            borderWidth: 2,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                            tension: 0,
                            order: 3
                          },
                          {
                            label: 'Trade Connections',
                            data: (() => {
                              const connectionPoints: Array<{x: Date | null, y: number | null}> = [];
                              const trades = backtest.results?.tradeDetails || [];
                              
                              for (let i = 0; i < trades.length - 1; i++) {
                                if (trades[i].action === 'BUY' && trades[i + 1].action === 'SELL') {
                                  connectionPoints.push({
                                    x: new Date(trades[i].date),
                                    y: trades[i].price
                                  });
                                  connectionPoints.push({
                                    x: new Date(trades[i + 1].date),
                                    y: trades[i + 1].price
                                  });
                                  connectionPoints.push({ x: null, y: null }); // Break between segments
                                }
                              }
                              return connectionPoints;
                            })(),
                            borderColor: '#9333ea',
                            borderWidth: 2,
                            borderDash: [5, 5],
                            pointRadius: 0,
                            fill: false,
                            tension: 0,
                            order: 2,
                            showLine: true,
                            spanGaps: false
                          },
                          {
                            label: 'BUY',
                            data: (backtest.results?.tradeDetails || [])
                              .filter(t => t.action === 'BUY')
                              .map(t => ({
                                x: new Date(t.date),
                                y: t.price
                              })),
                            backgroundColor: '#16a34a',
                            borderColor: '#ffffff',
                            borderWidth: 2,
                            pointRadius: 8,
                            pointHoverRadius: 10,
                            showLine: false,
                            order: 1
                          },
                          {
                            label: 'SELL',
                            data: (backtest.results?.tradeDetails || [])
                              .filter(t => t.action === 'SELL')
                              .map(t => ({
                                x: new Date(t.date),
                                y: t.price
                              })),
                            backgroundColor: '#dc2626',
                            borderColor: '#ffffff',
                            borderWidth: 2,
                            pointRadius: 8,
                            pointHoverRadius: 10,
                            showLine: false,
                            order: 1
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'nearest',
                          intersect: false
                        },
                        plugins: {
                          legend: {
                            display: true,
                            position: 'top' as const,
                            labels: {
                              usePointStyle: true,
                              padding: 15,
                              font: {
                                size: 12
                              }
                            }
                          },
                          tooltip: {
                            callbacks: {
                              title: function(context) {
                                return new Date(context[0].parsed.x).toLocaleString();
                              },
                              label: function(context) {
                                const label = context.dataset.label || '';
                                const numValue = context.parsed.y;
                                
                                // Format value based on price range
                                let value;
                                if (numValue < 1) {
                                  value = '$' + numValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 6
                                  });
                                } else if (numValue < 10) {
                                  value = '$' + numValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4
                                  });
                                } else {
                                  value = '$' + numValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  });
                                }
                                
                                if (label === 'BUY' || label === 'SELL') {
                                  const trades = backtest.results?.tradeDetails || [];
                                  const trade = trades[context.dataIndex];
                                  return [
                                    label + ': ' + value,
                                    trade?.reason || ''
                                  ];
                                }
                                return label + ': ' + value;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            type: 'time' as const,
                            time: {
                              unit: 'day',
                              displayFormats: {
                                day: 'MMM d'
                              }
                            },
                            title: {
                              display: true,
                              text: 'Time',
                              font: {
                                size: 14
                              }
                            },
                            grid: {
                              color: '#9CA3AF'
                            },
                            ticks: {
                              maxRotation: 45,
                              minRotation: 45
                            }
                          },
                          y: {
                            title: {
                              display: true,
                              text: 'Price ($)',
                              font: {
                                size: 14
                              }
                            },
                            grid: {
                              color: '#9CA3AF'
                            },
                            ticks: {
                              callback: function(value) {
                                const numValue = value as number;
                                // For values less than 1, show more decimals
                                if (numValue < 1) {
                                  return '$' + numValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 3,
                                    maximumFractionDigits: 6
                                  });
                                } else if (numValue < 10) {
                                  return '$' + numValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 4
                                  });
                                } else {
                                  return '$' + numValue.toLocaleString('en-US', {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 2
                                  });
                                }
                              }
                            }
                          }
                        }
                      }}
                    />
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No price data available for this backtest period
                  </p>
                )}
              </div>

              {/* Additional Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Total Trades</div>
                  <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                    {backtest.totalTrades}
                  </div>
                  <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                    W: {backtest.winningTrades} / L: {backtest.losingTrades}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Avg Trade</div>
                  <div className={`mt-1 text-xl font-bold ${!isNaN(backtest.results.avgTrade) && backtest.results.avgTrade > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {isNaN(backtest.results.avgTrade) || backtest.results.avgTrade === null ? 'N/A' : `${backtest.results.avgTrade > 0 ? '+' : ''}${backtest.results.avgTrade.toFixed(2)}%`}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Largest Win</div>
                  <div className="mt-1 text-xl font-bold text-green-600">
                    {isNaN(backtest.results.largestWin) || backtest.results.largestWin === null ? 'N/A' : `+${backtest.results.largestWin.toFixed(2)}%`}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Largest Loss</div>
                  <div className="mt-1 text-xl font-bold text-red-600">
                    {isNaN(backtest.results.largestLoss) || backtest.results.largestLoss === null ? 'N/A' : `${backtest.results.largestLoss.toFixed(2)}%`}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Profit Factor</div>
                  <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                    {isNaN(backtest.results.profitFactor) || backtest.results.profitFactor === null ? 'N/A' : backtest.results.profitFactor.toFixed(2)}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Volatility</div>
                  <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                    {isNaN(backtest.results.volatility) || backtest.results.volatility === null ? 'N/A' : `${backtest.results.volatility.toFixed(2)}%`}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Initial Balance</div>
                  <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(backtest.initialBalance)}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Final Balance</div>
                  <div className="mt-1 text-xl font-bold text-gray-900 dark:text-white">
                    {formatCurrency(backtest.finalBalance)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'trades' && (
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  All Trades ({backtest.results?.tradeDetails?.length || 0})
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Symbol
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Quantity
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Price
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Total
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Fees
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Reason
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {(backtest.results?.tradeDetails || []).map((trade, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div>{new Date(trade.date).toLocaleDateString()}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {new Date(trade.date).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded">
                            {backtest.symbol}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 text-xs font-semibold rounded ${
                              trade.action === 'BUY'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            }`}
                          >
                            {trade.action}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                          {trade.amount.toFixed(8)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                          {formatCryptoPrice(trade.price)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                          {formatCurrency(trade.value)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                          {formatCurrency(trade.fees)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                            Filled
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400 max-w-xs truncate" title={trade.reason}>
                          {trade.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'equity' && (
            <div className="space-y-6">
              {/* Equity Curve Chart */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Portfolio Equity Curve
                </h3>
                {(backtest.results?.equityCurve || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={350}>
                    <AreaChart data={backtest.results?.equityCurve || []}>
                      <defs>
                        <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.3} />
                      <XAxis
                        dataKey="date"
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString().split('/').slice(0, 2).join('/')}
                      />
                      <YAxis
                        tick={{ fill: '#9CA3AF', fontSize: 12 }}
                        tickFormatter={(value) => `$${value.toLocaleString()}`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#1F2937',
                          border: '1px solid #374151',
                          borderRadius: '8px',
                        }}
                        labelStyle={{ color: '#F9FAFB' }}
                        formatter={(value: any) => [`$${Number(value).toFixed(2)}`, 'Portfolio Value']}
                        labelFormatter={(label) => new Date(label).toLocaleString()}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke="#3B82F6"
                        strokeWidth={2}
                        fill="url(#colorEquity)"
                        name="Portfolio Value"
                        isAnimationActive={false}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No equity curve data available
                  </p>
                )}
              </div>

              {/* Drawdown Analysis */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Drawdown Analysis
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Max Drawdown</div>
                    <div className="mt-1 text-2xl font-bold text-red-600">
                      -{Math.abs(backtest.maxDrawdown).toFixed(2)}%
                    </div>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      Worst peak-to-trough decline
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Risk-Adjusted Return</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                      {isNaN(backtest.sharpeRatio) || backtest.sharpeRatio === null ? 'N/A' : backtest.sharpeRatio.toFixed(2)}
                    </div>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      Sharpe Ratio
                    </div>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">Profit Consistency</div>
                    <div className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                      {isNaN(backtest.results.profitFactor) || backtest.results.profitFactor === null ? 'N/A' : backtest.results.profitFactor.toFixed(2)}
                    </div>
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      Profit Factor (Wins/Losses)
                    </div>
                  </div>
                </div>
              </div>

              {/* Win/Loss Distribution */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Trade Distribution
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                    <div className="text-sm font-medium text-green-700 dark:text-green-400">Winning Trades</div>
                    <div className="mt-2 flex items-baseline">
                      <div className="text-3xl font-bold text-green-600">
                        {backtest.winningTrades}
                      </div>
                      <div className="ml-2 text-sm text-green-600">
                        ({isNaN(backtest.winRate) || backtest.winRate === null ? 'N/A' : `${backtest.winRate.toFixed(1)}%`})
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-green-700 dark:text-green-400">
                      Largest Win: {isNaN(backtest.results.largestWin) || backtest.results.largestWin === null ? 'N/A' : `+${backtest.results.largestWin.toFixed(2)}%`}
                    </div>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
                    <div className="text-sm font-medium text-red-700 dark:text-red-400">Losing Trades</div>
                    <div className="mt-2 flex items-baseline">
                      <div className="text-3xl font-bold text-red-600">
                        {backtest.losingTrades}
                      </div>
                      <div className="ml-2 text-sm text-red-600">
                        ({isNaN(backtest.winRate) || backtest.winRate === null ? 'N/A' : `${(100 - backtest.winRate).toFixed(1)}%`})
                      </div>
                    </div>
                    <div className="mt-2 text-xs text-red-700 dark:text-red-400">
                      Largest Loss: {isNaN(backtest.results.largestLoss) || backtest.results.largestLoss === null ? 'N/A' : `${backtest.results.largestLoss.toFixed(2)}%`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BacktestDetailsModal;

